import {
  API_ENDPOINT,
  API_ENDPOINT_V3,
  API_GATEWAY_ENDPOINT,
  API_GATEWAY_ENDPOINT_V3,
  CLOUD_FRONT_ABSOLUTE_ENDPOINT,
  CODEBUILD_BUILD_NUMBER,
  CODEBUILD_GIT_SHORT_ID,
  EARNINGS_CALL_DOT_DEV_ENDPOINT,
  ATLAS_FLEET_ENDPOINT,
  OAUTH_API_ENDPOINT,
} from "./api-config.mjs";

// https://github.com/JustinBeckwith/retry-axios
import * as rax from "retry-axios";
import axios from "axios";
import { addDays, subDays, startOfDay, endOfDay } from "date-fns";

// https://daveceddia.com/multiple-environments-with-react/

import log from "loglevel";
import { getSymbol } from "./components/Symbols.mjs";
import { getBehaviorSignalHeader } from "./utils/behaviorSignalGlobal.mjs";
import { forgetCrmReads } from "./utils/crmReads.mjs";

rax.attach();

async function doGet(url = "") {
  const response = await fetch(url);
  if (!response.ok) {
    let error = {
      status: response.status,
      statusText: response.statusText,
    };
    error.responseBody = await response.text();
    log.warn("doGet()", error);
    throw error;
  }
  return await response.json();
}

export async function connectWebSocket(url, options = {}) {
  const {
    onOpen = () => {},
    onMessage = () => {},
    onError = () => {},
    onClose = () => {},
    protocols = []
  } = options;

  const ws = new WebSocket(url, protocols);

  ws.onmessage = (event) => {
    log.debug('WebSocket message received:', event.data);
    onMessage(event);
  };

  ws.onerror = (event) => {
    log.error('WebSocket error:', event);
    onError(event);
  };

  ws.onclose = (event) => {
    log.debug('WebSocket closed');
    onClose(event);
  };

  // Wait for connection to open
  await new Promise((resolve, reject) => {
    ws.onopen = (event) => {
      log.debug('WebSocket connected');
      onOpen(event);
      resolve();
    };
    ws.onerror = (event) => {
      reject(event);
    };
  });

  return ws;
}

export async function sendWebSocketMessage(ws, message) {
  if (ws.readyState === WebSocket.OPEN) {
    try {
      ws.send(typeof message === 'string' ? message : JSON.stringify(message));
    } catch (error) {
      log.error('Error sending WebSocket message:', error);
      throw error;
    }
  } else {
    throw new Error('WebSocket is not open');
  }
}

export async function fetchEarnings(
  last_evaluated_key = null,
  event_id = null,
  filter_condition = null
) {
  let queryParams = "";
  if (last_evaluated_key) {
    queryParams = `last_evaluated_key=${last_evaluated_key}`;
  }
  if (event_id) {
    queryParams = `event_id=${event_id}`;
  }
  if (filter_condition) {
    if (!!queryParams) {
      queryParams += `&${filter_condition}`;
    } else {
      queryParams = filter_condition;
    }
  }

  const url = `${API_ENDPOINT_V3}/earnings?${queryParams}`;
  log.debug("loading url", url);
  const response = await fetch(url);
  if (!response.ok) {
    log.warn("Response Not OK:", response);
    throw response;
  }
  return response.json();
}

function authHeaderPair(xAuthType, xAuthToken) {
  return {
    "x-auth-type": xAuthType,
    "x-auth-token": xAuthToken,
  };
}

// A client-built `x-auth-token`: base64 of a JSON object, which the backend reads with
// json.loads(base64.b64decode(token)) (cca/security/auth.py, _decode_auth_token).
//
// btoa alone throws InvalidCharacterError on any character above U+00FF, and a password
// is whatever someone typed. So every character outside ASCII is written as a JSON
// \uXXXX escape first: what btoa is handed is plain ASCII whatever the input, and it
// cannot throw. json.loads turns the escapes back (two of them for a character outside
// the BMP, which it joins), so the server compares exactly what was typed.
export function encodeAuthToken(payload) {
  const asciiJson = JSON.stringify(payload).replace(
    /[\u007f-\uffff]/g,
    (character) => `\\u${character.charCodeAt(0).toString(16).padStart(4, "0")}`
  );
  return btoa(asciiJson);
}

// The auth type of the password sign-in, as the backend names it (REVIEWER_AUTH_TYPE in
// cca/security/reviewer.py). It works for one dedicated account and is accepted by
// POST /a2/login only.
const PASSWORD_AUTH_TYPE = "reviewer";

export function extractAuthHeaders({ context, passwordCredentials }) {
  // The password sign-in hands its credential in as an argument, never in a
  // sessionContext. Everything read from the context below is something the login
  // components go on to store (they spread the pre-login context into the one they save
  // to localStorage), and a password must not be stored. See newSessionWithPassword.
  if (passwordCredentials) {
    const { email, password } = passwordCredentials;
    return authHeaderPair(PASSWORD_AUTH_TYPE, encodeAuthToken({ email, password }));
  }
  const { sessionContext } = context || {};
  const {
    session,
    google,
    facebook,
    test,
    appBuilderKey,
    loginWithEmail,
  } = sessionContext || {};

  if (session) {
    return authHeaderPair("session", session.session_id);
  } else if (google) {
    return authHeaderPair("google-web-code", google.code);
  } else if (facebook) {
    return authHeaderPair("facebook-web", facebook.accessToken);
  } else if (test) {
    return authHeaderPair("test", test.testAuthToken);
  } else if (appBuilderKey) {
    return authHeaderPair("app-builder", appBuilderKey);
  } else if (loginWithEmail) {
    return authHeaderPair("email", btoa(JSON.stringify(loginWithEmail)));
  }
  return {};
}

async function getRequestHeaders(params) {
  const headers = {
    ...extractAuthHeaders(params),
    "x-client-build-version": CODEBUILD_BUILD_NUMBER,
    "x-client-commit-id": CODEBUILD_GIT_SHORT_ID,
    "x-client-device-type": "web",
  };

  // Add behavior signal with nonce if available
  const behaviorSignal = await getBehaviorSignalHeader();
  if (behaviorSignal) {
    headers["x-behavior-signal"] = behaviorSignal;
  }

  return headers;
}

export async function doAxiosGet(params) {
  return await doAxiosRequest("get", params);
}

export async function doAxiosPost(params) {
  return await doAxiosRequest("post", params);
}

export async function doAxiosDelete(params) {
  return await doAxiosRequest("delete", params);
}

export async function doAxiosRequest(method, params) {
  const {
    url,
    queryParams = {},
    data,
    cancelToken,
    signal,
  } = params;
  // log.debug("loading url", url, queryParams, data, params);
  try {
    const headers = await getRequestHeaders(params);
    const { data: result } = await axios({
      method,
      url,
      params: queryParams,
      data,
      headers,
      cancelToken,
      signal,
      // timeout: 30000,
    });
    return result;
  } catch (error) {
    // https://github.com/axios/axios#handling-errors
    log.error(
      `doAxiosRequest() failed for ${method}:${url} ${JSON.stringify(
        queryParams
      )}`
    );
    if (error.response) {
      log.error(`Server responded with HTTP status: ${error.response.status}`);
      // A failed request (e.g. a 404 that returns the entire SPA HTML) can have
      // a huge response body; logging it whole floods the logs. Truncate it.
      const MAX_BODY_LOG_CHARS = 1000;
      let body = error.response.data;
      if (typeof body !== "string") {
        try {
          body = JSON.stringify(body);
        } catch {
          body = String(body);
        }
      }
      log.error(
        body && body.length > MAX_BODY_LOG_CHARS
          ? `Server response body (${body.length} chars, truncated): ${body.slice(0, MAX_BODY_LOG_CHARS)}…`
          : `Server response body: ${body}`
      );
      log.error(error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      log.error("The request was made but no response was received.");
      log.error(error.request);
    } else {
      log.error("Something happened in setting up the request that triggered an Error");
      log.error("Error", error.message);
    }
    throw error;
  }
}

export async function getPageData(params) {
  const { pageDataPath } = params;
  const url = `/page-data/${pageDataPath}/page-data.json`;
  return doAxiosGet({ url, ...params });
}

export async function getECSBuildTasks(params) {
  return doAxiosGet({ url: `${API_ENDPOINT}/build/tasks`, ...params });
}

export async function getMobileAppBuilds(params) {
  return doAxiosGet({ url: `${API_ENDPOINT}/mobile/builds`, ...params });
}

export async function getNews(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/news`, ...params });
}

export async function userAuthSetup(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT}/user/auth/setup`,
    ...params,
  });
}

export async function newLeadForm(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT}/intake/lead`,
    ...params,
  });
}

// Quiz funnel (see cca documentation/32-quiz-funnel-and-self-improvement.md). One endpoint:
// GET /a2/quiz starts a session, POST /a2/quiz dispatches on `operation`. The operation is
// injected here rather than by callers, so a page just calls submitQuizAnswer/completeQuiz.
//
// The backend issues the session id and decides which phrasing of each question this visitor
// sees, so the client must not cache or reuse a session across visits -- that would put one
// respondent into both arms of a running split test.
export async function startQuiz(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/quiz`, ...params });
}

export async function submitQuizAnswer(params) {
  const { data, ...rest } = params;
  return await doAxiosPost({
    url: `${API_ENDPOINT}/quiz`,
    data: { ...data, operation: "answerQuestion" },
    ...rest,
  });
}

export async function completeQuiz(params) {
  const { data, ...rest } = params;
  return await doAxiosPost({
    url: `${API_ENDPOINT}/quiz`,
    data: { ...data, operation: "completeQuiz" },
    ...rest,
  });
}

export async function postNews(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/news`, ...params });
}

export async function getEarnings(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT_V3}/earnings`, ...params });
}

export async function getQuartrCalendar(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/quartr/calendar`, ...params });
}

export async function getQuartrEventDetails(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/quartr/event/details`, ...params });
}

export async function getQuartrCompanySearch(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/quartr/search/company`, ...params });
}

export async function newEarnings(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/earnings`, ...params });
}

export async function deleteEarnings(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT_V3}/earnings`, ...params });
}

export async function callUseSlideDeckUrl(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/earnings/use/slides`, ...params });
}

export async function findSlideDeck(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/earnings/find-slide-deck`, ...params });
}

export async function getNotifications(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/notifications`, ...params });
}

export async function saveNotification(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications`, ...params });
}

export async function getBlog(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/blog`, ...params });
}

export async function saveBlog(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/blog`, ...params });
}

export async function deleteBlog(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/blog`, ...params });
}

export async function recordEarnings(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT_V3}/earnings/record`,
    ...params,
  });
}

// Alpha-only. Launches a long-form (~50 min) LIVEDEMO test recording so live
// features (livestream, live viewer, live audio) can be exercised without waiting
// on a real call. Backs the "Start test recording" button on the admin /test page.
// The endpoint is registered at /a2/test/recording only (no /a3 clone), so this
// must use API_ENDPOINT (not API_ENDPOINT_V3).
export async function startTestRecording(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/test/recording`, ...params });
}

export async function symbolsAdmin(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/symbols/admin`, ...params });
}

export async function buildWebApp(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/build/app`, ...params });
}

export async function chatApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/chat`, ...params });
};

export async function getChatHistory(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/chat/history`, ...params });
};

export async function deleteChatConversation(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/chat/history`, ...params });
};

export async function getChatConfig(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/chat/config`, ...params });
};

export async function saveChatConfig(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/chat/config`, ...params });
};

export async function paymentApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/payment`, ...params });
};

// Admin-only. Backs the /admin/coupons page; operations: listCoupons,
// createCoupon, deactivateCoupon, and applyCouponToSubscription (attach a
// coupon to any customer's existing subscription — used by the admin
// CustomerDetail view). Customer-side coupon validation and self-service
// apply happen through paymentApi (operations: "validateCoupon",
// "previewApplyCoupon", "applyCoupon").
export async function couponApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/admin/coupons`, ...params });
};

// Dev-only: the integration-test data endpoint backs both the integration
// suite's createCustomer flow and the new "Login as Random User" button on
// /login. Backend gates createRandomTestUser to non-prod stages.
export async function integTestData(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/integ/test/data`, ...params });
};

export async function transcribeEarnings(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT_V3}/earnings/transcribe`,
    ...params,
  });
}

export async function notifyUsersEventPublished(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT_V3}/earnings/notify`,
    ...params,
  });
}

export async function subscribeEmail(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT}/subscribe/email`,
    ...params,
  });
}

export async function getSubscription(params) {
  const { queryParams } = params || {};
  const combinedQueryParams = {
    query_type: `subscription`,
    ...queryParams,
  };
  return await doAxiosGet({ url: `${API_ENDPOINT}/web/subscription`, ...params, queryParams: combinedQueryParams });
};

export async function deleteSubscription(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/web/subscription`, ...params });
};

export async function getInvoices(params) {
  const { queryParams } = params || {};
  const combinedQueryParams = {
    query_type: `invoice`,
    ...queryParams,
  };
  return await doAxiosGet({ url: `${API_ENDPOINT}/web/subscription`, ...params, queryParams: combinedQueryParams });
};

export async function getInvoiceLink(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/web/invoice`, ...params });
};

export async function getApiKey(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/api/keys`, ...params });
}

export async function generateApiKey(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/api/keys`, ...params });
}

export async function deleteApiKey(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/api/keys`, ...params });
}

// Admin-only trial API key operations. These reuse the /api/keys endpoint and
// are dispatched by the `operation` field (admin_generate_trial_api_key,
// admin_list_trial_api_keys, admin_revoke_trial_api_key); the backend gates them
// with an admin-role check.
// Trial API keys are served by the dedicated admin endpoint (/a2/admin/api-keys),
// which dispatches on HTTP method (GET list / POST generate / DELETE revoke) — not
// on an `operation` field. It is admin-role gated and needs no subscription.
export async function adminListTrialApiKeys(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT_V3}/admin/api-keys`, ...params });
}

export async function adminGenerateTrialApiKey(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/admin/api-keys`, ...params });
}

export async function adminRevokeTrialApiKey(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT_V3}/admin/api-keys`, ...params });
}

export async function getProfile(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/profile`, ...params });
}

export async function saveProfile(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/profile`, ...params });
}

export async function deleteProfile(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/profile`, ...params });
}

export async function fetchCompany(exchange, symbol) {
  const url = `${API_ENDPOINT}/company?exchange=${exchange}&symbol=${symbol}`;
  return await doGet(url);
}

export async function getCompany(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/company`, ...params });
}

export async function updateCompany(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/company`, ...params });
}

export async function getSiteStats(appBuilderKey, params) {
  // console.log("getSiteStats:", params);
  let res = await doAxiosGet({
    url: `${API_GATEWAY_ENDPOINT}/site/stats`,
    context: {
      sessionContext: {
        appBuilderKey,
      },
    },
    ...params,
  });
  // console.log("Site stats:", res);
  if (!res) {
    console.error(
      "The backend API returned null siteStats!  This is unexpected."
    );
    return {
      earnings_events_recorded: 1,
      companies: 1,
      events_by_day: [],
      published_events_by_day: [],
    };
  }
  return res;
}

export async function getSP500Companies(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/companies/sp500`, ...params });
};

export const earningsQuery = async (queryParams, appBuilderKey) => {
  const results = await getEarnings({
    url: `${API_GATEWAY_ENDPOINT_V3}/earnings`,
    queryParams: {
      params: JSON.stringify(queryParams),
    },
    context: {
      sessionContext: {
        appBuilderKey,
      },
    },
  });
  // console.log("earningsResults:", results);
  return results.earnings.map(
    ({ quarter, year, exchange, symbol, primary_conference_date }) => {
      return {
        exchange,
        quarter: quarter.toUpperCase(),
        year,
        symbol,
        conference_date: primary_conference_date,
      };
    }
  );
};

const earningsWithCompanyName = async (queryParams, appBuilderKey) => {
  const result = await earningsQuery(queryParams, appBuilderKey);
  // console.log("earningsWithCompanyName queryParams: ", JSON.stringify(queryParams));
  // console.log("earningsWithCompanyName result:", result);
  const symbolsConfig = {
    endpoint: CLOUD_FRONT_ABSOLUTE_ENDPOINT,
  };
  // Ensure all values are non-null, truthy
  const filteredResult = result.filter(
    ({ quarter, year, exchange, symbol, conference_date }) =>
      !!quarter && !!year && !!exchange && !!symbol && !!conference_date
  );
  const allResults = await Promise.all(
    filteredResult.map(async ({ quarter, year, exchange, symbol, conference_date }) => {
      // console.log("Exchange:", exchange, "Symbol:", symbol);
      // TODO: First lookup _sym in symbols-v2, only if not found look up in all symbols
      let _sym = await getSymbol(exchange, symbol, symbolsConfig);
      // console.log("_sym", _sym);
      const { name = null } = _sym || {};
      if (!name) {
        log.warn(`Could not find company name for: ${exchange}_${symbol}`);
      }
      return {
        quarter,
        year,
        exchange,
        symbol,
        companyName: name,
        conference_date,
      };
    })
  );
  return allResults.filter(
    ({ quarter, year, exchange, symbol, companyName, conference_date }) =>
      quarter && year && exchange && symbol && companyName && conference_date
  ); // Ensure all values are non-null, truthy
};

export const getRecentCalls = async (appBuilderKey) => {
  const queryParams = {
    sort_by: "recording_stop_date",
    fields_present: [
      "audio_file",
      "exchange",
      "symbol",
      "primary_conference_date",
      "year",
      "quarter",
    ],
    fields_not_present: [],
    match: {
      is_published: true,
      primary: true,
    },
    size: 5,
  };
  log.debug("getRecentCalls()", queryParams);
  return await earningsWithCompanyName(queryParams, appBuilderKey);
};

export const earningsCallsQueryParams = () => {
  return {
    sort_by: "primary_conference_date",
    sort_order: "asc",
    fields_present: [
      "exchange",
      "symbol",
      "primary_conference_date",
      "year",
      "quarter",
    ],
    fields_not_present: [],
    match: {
      primary: true,
    },
    not_match: {
      news_wire: "test",
      no_conference_call: true,
    },
    filters: [hasConferenceFilter()],
    size: 1000,
  };
};

const hasConferenceFilter = () => {
  return {
    bool: {
      should: [
        {
          match_phrase: {
            has_teleconference: true,
          },
        },
        {
          match_phrase: {
            has_web_cast: true,
          },
        },
      ],
      minimum_should_match: 1,
    },
  };
};

export const getUpcomingCalls = async (appBuilderKey) => {
  log.debug("getUpcomingCalls()");
  const dateRangeFilter = {
    primary_conference_date: {
      format: "strict_date_optional_time",
      gte: new Date().toISOString(),
    },
  };
  const queryParams = {
    ...earningsCallsQueryParams(),
    range_filter: [dateRangeFilter],
  };
  return await earningsWithCompanyName(queryParams, appBuilderKey);
};

export const getEarningsCalendar = async ({
  startingDate = new Date(),
  daysBack = 30,
  daysForward = 30,
  appBuilderKey,
}) => {
  // console.log("earningsCalendar()");
  const dateRangeFilter = {
    primary_conference_date: {
      format: "strict_date_optional_time",
      gte: startOfDay(subDays(startingDate, daysBack)).toISOString(),
      lte: endOfDay(addDays(startingDate, daysForward)).toISOString(),
    },
  };
  const queryParams = {
    ...earningsCallsQueryParams(),
    range_filter: [dateRangeFilter],
    size: 2500,
  };
  return await earningsWithCompanyName(queryParams, appBuilderKey);
};

export async function identifyDevice(params) {
  return doAxiosGet({ url: `${API_ENDPOINT}/id`, ...params });
};

export async function newSession(params) {
  return doAxiosPost({ url: `${API_ENDPOINT}/login`, data: {}, ...params });
};

// Sign in with an email and a password: the same POST /a2/login as every other sign-in
// (newSession above), with `x-auth-type: reviewer` and the credentials in `x-auth-token`.
// Answers what any login answers, `{isAdminUser, session, account}`. Every refusal is the
// same 401: a wrong email, a wrong password, a malformed token. A backend that predates
// the sign-in answers 401 too, for an auth type it does not know. The one account and its
// credential are defined in the backend ("Reviewer Password (one account)" in the cca
// repo's documentation/10-accounts-and-authentication.md); this site holds neither.
//
// The email goes as typed (the backend trims and lowercases it). The password goes as
// typed too, and must: it is compared exactly.
//
// A failure is rethrown as a plain Error carrying the HTTP status and nothing else. What
// axios rejects with holds the request it failed on, `x-auth-token` included
// (error.config.headers), and callers log what they catch. Stripping it here is what
// keeps the password out of a log line, and out of a session recording's console
// capture, whatever the caller does with the error. `status` is undefined when no
// response came back at all.
//
// Not retried on failure, by rax's default httpMethodsToRetry (POST is absent from it).
export async function newSessionWithPassword({ email, password }) {
  try {
    return await newSession({ passwordCredentials: { email, password } });
  } catch (error) {
    throw Object.assign(new Error("The password sign-in request failed"), {
      status: error?.response?.status,
    });
  }
}

export async function destroySession(params) {
  return doAxiosPost({ url: `${API_ENDPOINT}/logout`, data: {}, ...params });
};

export async function updateAccountProfile({ firstName, lastName, company, context }) {
  return doAxiosPost({
    url: `${API_ENDPOINT}/account`,
    data: { first_name: firstName, last_name: lastName, company },
    context,
  });
};

export async function getTranscript(args, context) {
  // console.log(" -> getTranscript:", transcription_file);
  // console.log(`${API_GATEWAY_ENDPOINT_V3}/transcript`);
  return await doAxiosGet({
    url: `${API_GATEWAY_ENDPOINT_V3}/transcript`,
    queryParams: {
      ...args,
    },
    context,
  });
};

export async function recordAnalyticsEvents(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/tk`, ...params });
};

export async function pageLoadEvent(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/pg`, ...params });
};

export async function fileUpload(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/file/upload`, ...params });
};

export async function brockTestApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/brockTest`, ...params });
};

export async function getUsersAdmin(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT_V3}/users/admin`, ...params });
};

export async function adminUserRoles(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/users/admin`, ...params });
};

/**
 * Admin control plane for live transcription (EarningsCall/cca `GET /a3/admin/live-transcription`).
 *
 * Returns both switches -- `LIVE_TRANSCRIPTION_ON_RECORDER` and `LIVE_TRANSCRIPT_PUSH_ENABLED`
 * -- read straight from SSM Parameter Store on every call, plus the calls currently streaming
 * and whether each one is being transcribed.
 *
 * Each switch carries `version` and `last_modified`. They are not decoration: Parameter Store
 * is eventually consistent, so a read taken right after a write can legitimately still show
 * the old value, and the version is how the caller tells "not changed yet" apart from "not
 * changed at all". See `setLiveTranscriptionSwitch`.
 *
 * Admin only -- an annotator gets 403.
 */
export async function getLiveTranscriptionSwitches(params) {
  return await doAxiosGet({
    url: `${API_ENDPOINT_V3}/admin/live-transcription`,
    ...params
  });
};

/**
 * Flip one live-transcription switch (`POST /a3/admin/live-transcription`).
 *
 * `data: { switch: "<NAME>", enabled: <boolean> }`. `enabled` must be a real boolean: the
 * backend refuses a string rather than coercing it, because `Boolean("false")` is `true` and
 * coercion there would turn "turn it off in prod" into its opposite.
 *
 * **Render the response, do not re-fetch to confirm it.** The response carries the state the
 * backend wrote and the new parameter version. A confirming GET can race Parameter Store's
 * eventual consistency and return the pre-write value, which would flip the toggle back under
 * the operator's cursor and read as a broken control.
 *
 * Note also that the parameter changing and the behaviour changing are separate events -- each
 * switch reports its own lag in `takes_effect`. The recorder reads its switch once per job, so
 * a flip applies from the next recording and never to a call already streaming.
 */
export async function setLiveTranscriptionSwitch(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT_V3}/admin/live-transcription`,
    ...params
  });
};

export async function getLiveEvents(params) {
  return await doAxiosGet({
    url: `${EARNINGS_CALL_DOT_DEV_ENDPOINT}/live-events`,
    ...params
  });
};

/**
 * Provisional live transcript for an in-progress earnings call, keyed by the opaque HLS
 * session `token` -- the same token embedded in the event's streamingUrl
 * (`.../live/{token}/playlist.m3u8`). Public + unauthenticated: same trust level as the
 * HLS playlist. Backend: EarningsCall/cca `GET /a3/live-transcript/{token}`.
 *
 * `/a3`, not `/a2`: cca#1039 serves this route at `/a3` and retired the `/a2` one, which now
 * answers API Gateway's `{"message":"Not Found"}` (verified on alpha 2026-09-20).
 *
 * This builds the URL from a token, which is the FALLBACK. A caller holding the `pollUrl`
 * the `/live` API advertises per event should pass that to `getLiveTranscriptWithMeta`
 * instead -- the URL is the backend's to choose.
 */
export function liveTranscriptUrl({ token, cursor = 0 }) {
  return `${API_GATEWAY_ENDPOINT_V3}/live-transcript/${encodeURIComponent(token)}?cursor=${cursor}`;
}

/** `cursor` applied to a poll URL that may already carry a query string. */
export function withCursor(pollUrl, cursor = 0) {
  const url = new URL(pollUrl);
  url.searchParams.set("cursor", String(cursor));
  return url.toString();
}

/**
 * Fetches the live transcript, handing back the transport facts alongside the
 * envelope: `{ url, httpStatus, elapsedMs, body }`. The Live Events monitor's polling
 * panel exists to display exactly those -- a 404 and a round-trip time are its output, not
 * failures to be hidden -- so it needs the response, not just the JSON.
 *
 * Takes `url` (the `pollUrl` the `/live` API advertises per event) in preference to
 * `token`. The URL is the backend's to choose: it publishes `/a3/live-transcript/{token}`
 * on the site host, and a client that rebuilds that path itself will be wrong the day the
 * backend moves it.
 *
 * `credentials: "omit"` is load-bearing, not tidiness. `pollUrl` is on the SITE host, so a
 * logged-in browser would attach the session cookie by default -- and this endpoint's
 * Lambda has a narrow grant with no read on the session table, so it answers 500 rather
 * than ignoring the cookie. Nothing here is authenticated (the opaque HLS token is the
 * key), so there is never a reason to send one.
 *
 * Deliberately NOT retried in-client, which is the one documented exception to this repo's
 * retry rule (CLAUDE.md, "Outbound HTTP"): every caller polls on an interval, so the next
 * tick IS the retry, and a retry inside the call would fold two round trips into one
 * `elapsedMs` and hide the very failure the monitor is there to show.
 */
export const LIVE_TRANSCRIPT_TIMEOUT_MS = 15_000;

/**
 * An AbortSignal that fires when `signal` does OR when `timeoutMs` elapses.
 *
 * Written out rather than using `AbortSignal.any` so it works in jsdom and older browsers.
 * The timeout is the load-bearing half: `fetch` has no default timeout, so without it a
 * connection that opens and then stalls never settles, and a caller enforcing single-flight
 * holds its guard for as long as the network cares to keep the socket open.
 */
function abortAfter(signal, timeoutMs) {
  const controller = new AbortController();
  const onAbort = () => controller.abort(signal.reason);
  if (signal) {
    if (signal.aborted) controller.abort(signal.reason);
    else signal.addEventListener("abort", onAbort, { once: true });
  }
  const timer = setTimeout(
    () => controller.abort(new Error(`live-transcript request timed out after ${timeoutMs}ms`)),
    timeoutMs,
  );
  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timer);
      if (signal) signal.removeEventListener("abort", onAbort);
    },
  };
}

export async function getLiveTranscriptWithMeta({
  url: pollUrl,
  token,
  cursor = 0,
  signal,
  timeoutMs = LIVE_TRANSCRIPT_TIMEOUT_MS,
}) {
  const url = pollUrl ? withCursor(pollUrl, cursor) : liveTranscriptUrl({ token, cursor });
  const startedAt = Date.now();
  const abort = abortAfter(signal, timeoutMs);
  let response;
  try {
    response = await fetch(url, {
      cache: "no-store",
      credentials: "omit",
      headers: { Accept: "application/json" },
      signal: abort.signal,
    });
  } finally {
    abort.cleanup();
  }
  const elapsedMs = Date.now() - startedAt;
  const meta = { url, httpStatus: response.status, elapsedMs };
  if (response.status === 404) {
    return { ...meta, body: { status: "pending", next_cursor: cursor, segments: [] } };
  }
  if (!response.ok) {
    const error = { status: response.status, statusText: response.statusText, ...meta };
    try {
      error.responseBody = await response.text();
    } catch (e) {
      // ignore body read failures
    }
    log.warn("getLiveTranscriptWithMeta()", error);
    throw error;
  }
  return { ...meta, body: await response.json() };
};

export async function transcriptionSearch(params) {
  return await doAxiosGet({ url: `${EARNINGS_CALL_DOT_DEV_ENDPOINT}/search`, ...params });
};

// The full transcript for a logged-in visitor, from the API at runtime -- the same
// GET the mobile app makes (cca-mapp transcript_service.dart), authenticated by the
// `x-auth-type: session` / `x-auth-token` pair every call here sends. Level 3 is the
// one with per-word start times, which the player needs to follow the audio; the
// body is mapped into the page's shape by transcriptFromApi (src/transcriptPreview.mjs).
// Not the cca /a2/transcript the build uses: that endpoint is admin/app-builder/
// annotator only. Anonymous -> 401, which is the login gate being enforced by the
// server rather than by a CSS clamp (see TRANSCRIPT_PREVIEW_ONLY in api-config.mjs).
export async function getTranscriptFromApi({ exchange, symbol, year, quarter, ...params }) {
  return await doAxiosGet({
    url: `${EARNINGS_CALL_DOT_DEV_ENDPOINT}/transcript`,
    queryParams: {
      exchange: String(exchange).toLowerCase(),
      symbol: String(symbol).toLowerCase(),
      year: String(year),
      quarter: String(quarter).toLowerCase(),
      level: 3,
    },
    ...params,
  });
}

// The <audio> element reports only a MediaError code when a load fails, never
// the HTTP status behind it. After an auth-gated load has given up, this asks
// the media endpoint once, with the session headers the element could not
// carry, what it answers for the call: 429 is the WEB media bucket
// (api RateLimitConfig.webMediaConfig), 401 a session the cell cannot see yet,
// 5xx the host. Resolves to the status, or null when no response arrived. HEAD,
// so no audio bytes. Exactly one request: validateStatus keeps a non-2xx from
// throwing, and raxConfig turns off retry-axios (attached globally above),
// which by default resends a HEAD that got no response (twice). This is
// a diagnostic for the AudioLoadError analytics event, not a load.
export async function getAudioStatusFromApi({ exchange, symbol, year, quarter, context }) {
  const headers = await getRequestHeaders({ context });
  try {
    const response = await axios({
      method: "head",
      url: `${EARNINGS_CALL_DOT_DEV_ENDPOINT}/audio`,
      params: {
        exchange: String(exchange).toLowerCase(),
        symbol: String(symbol).toLowerCase(),
        year: String(year),
        quarter: String(quarter).toLowerCase(),
      },
      headers,
      validateStatus: () => true,
      raxConfig: { retry: 0 },
    });
    return response.status;
  } catch (error) {
    log.error("Audio status probe failed:", error);
    return null;
  }
}

export async function deEntitySearch(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/de-entity/search`, ...params });
};

export async function getAdminQuotes(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT_V3}/admin/quotes`, ...params });
};

export async function postAdminQuotes(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/admin/quotes`, ...params });
};

export async function getCustomerQuote(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/quotes`, ...params });
};

export async function getNotificationsV2(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

export async function markNotificationReadV2(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

export async function getContractStatus(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/quotes/accept-contract`, ...params });
};

export async function recordContractAcceptance(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/quotes/accept-contract`, ...params });
};

export async function markAllNotificationsReadV2(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

export async function createNotificationV2(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

export async function recordNotificationPopupShownV2(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

export async function adminListNotificationsV2(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

/**
 * Render the promo email and return its HTML WITHOUT sending it.
 *
 * The admin send page previews the return value and the send path transmits
 * the same render, so the preview cannot drift from what a recipient receives.
 * Admin-only, like create and admin_list.
 */
export async function renderPromoEmailV2(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

export async function pushSubscribeV2(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

export async function pushUnsubscribeV2(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

/**
 * Send a browser control command via HTTP.
 * @param {Object} params
 * @param {Object} params.data - { event_id, action, x?, y?, text?, keys?, scroll_x?, scroll_y? }
 */
export async function sendBrowserControlHttp(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT}/browser-control`,
    ...params,
  });
}

// The admin/billing actions that only read. Anything else may change what the CRM tabs keep
// (utils/crmReads.mjs), so it empties that store before it goes out and again once it lands.
const CRM_READ_ACTIONS = new Set([
  "crm-customer-summary", "crm-customer-facts", "crm-usage-series", "crm-free-users", "crm-analytics",
  "crm-pricing-conversion", "crm-roas", "crm-page-visits", "crm-tickets", "crm-ac-activity",
]);
const onlyReads = (action) => /^(list|get)-/.test(action || "") || CRM_READ_ACTIONS.has(action);

export async function adminBillingApi(params) {
  const writes = !onlyReads(params?.data?.action);
  if (writes) forgetCrmReads();
  try {
    return await doAxiosPost({ url: `${API_ENDPOINT_V3}/admin/billing`, ...params });
  } finally {
    if (writes) forgetCrmReads();
  }
};

// CRM actions all go through /admin/billing, which dispatches on `action` and
// carries the admin role gate. The handler json.loads list filters from
// `params`, so they are stringified here.
async function crmBillingApi(action, params) {
  const { data, ...rest } = params || {};
  const payload = { ...data, action };
  if (payload.params && typeof payload.params !== "string") {
    payload.params = JSON.stringify(payload.params);
  }
  return await adminBillingApi({ data: payload, ...rest });
}

// The customer list's summary tiles. No arguments.
export async function crmCustomerSummary(params) {
  return await crmBillingApi("crm-customer-summary", params);
}

// data: { email }
export async function crmCustomerFacts(params) {
  return await crmBillingApi("crm-customer-facts", params);
}

// data: { email, range }; range is "7d" | "30d" | "cycle" | "all".
export async function crmUsageSeries(params) {
  return await crmBillingApi("crm-usage-series", params);
}

// data: { params: { stage, kind, source, search, due_today, overdue,
//                   sort_by, sort_order, size, results_from } }
export async function listProspects(params) {
  return await crmBillingApi("list-prospects", params);
}

// data: { prospect_id }
export async function getProspect(params) {
  return await crmBillingApi("get-prospect", params);
}

// data: { prospect }. `created_by` comes from the session, never from here.
export async function createProspect(params) {
  return await crmBillingApi("create-prospect", params);
}

// data: { prospect_id, changes }
export async function updateProspect(params) {
  return await crmBillingApi("update-prospect", params);
}

// data: { prospect_id, stripe_customer_id }
export async function convertProspect(params) {
  return await crmBillingApi("convert-prospect", params);
}

// data: { prospect_id, text }
export async function addProspectNote(params) {
  return await crmBillingApi("add-prospect-note", params);
}

// data: { params: { search, status, sort_by, sort_order, size, results_from } }
export async function crmFreeUsers(params) {
  return await crmBillingApi("crm-free-users", params);
}

// data: { params: { date_from, date_to, group_by } }; group_by is "monthly" |
// "weekly" | "quarterly" | "yearly" (cca _GROUP_BY_INTERVALS).
export async function crmAnalytics(params) {
  return await crmBillingApi("crm-analytics", params);
}

// data: { params: { date_from, date_to } }. People who opened a pricing page, how many then
// paid, and the rate: the Analytics tab's Conversion rate card (cca/crm_pricing_conversion.py).
export async function crmPricingConversion(params) {
  return await crmBillingApi("crm-pricing-conversion", params);
}

// data: {}. Google Ads ROAS per month (cca/crm_roas.py); billing role only.
export async function crmRoas(params) {
  return await crmBillingApi("crm-roas", params);
}

// data: { month: "YYYY-MM", amount_cents }; a null amount_cents removes the month.
// Billing role only.
export async function crmSetAdSpend(params) {
  return await crmBillingApi("crm-ad-spend-set", params);
}

// data: { email, days }
export async function crmPageVisits(params) {
  return await crmBillingApi("crm-page-visits", params);
}

// data: { email }
export async function crmTickets(params) {
  return await crmBillingApi("crm-tickets", params);
}

// data: { email, subject, body }. `sent_by` comes from the session.
export async function crmSendEmail(params) {
  return await crmBillingApi("crm-send-email", params);
}

// data: { email }
export async function crmAcActivity(params) {
  return await crmBillingApi("crm-ac-activity", params);
}

export async function saveSecretAdmin(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/admin/secrets`, ...params });
};

export async function submitReviewG2Intent(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/review/g2-intent`, ...params });
};

export async function submitReviewFeedback(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/review/feedback`, ...params });
};

export async function adminReviewApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/admin/reviews`, ...params });
};

// Admin daily operational stats (per-day recording / publish-latency / slide-deck stats)
export async function adminDailyStatsApi(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT_V3}/admin/daily-stats`, ...params });
}
// Admin support-chat transcripts. List with { size, results_from }, or detail
// with { conversation_id } — same endpoint, the backend switches on the param.
export async function adminSupportChatsApi(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT_V3}/admin/support-chats`, ...params });
}
// Fleet-wide cache-reconciler status: one row per Atlas box, each carrying either
// that box's `GET /ops/cache/reconcile` body or the error that came back instead, so
// one unreachable region degrades to one red card rather than failing the page.
//
// `/ops/*`, NOT `/internal/*`: the split is the credential boundary. `/ops/*` takes
// an admin session, `/internal/*` stays token-only because a heap dump carries live
// API keys.
//
// Authenticated by the `x-auth-type: session` / `x-auth-token` pair every other call
// here sends. OpsAuthFilter accepts the session cookie too, but only from an
// earningscall.biz origin -- and the integration suite serves the app from
// localhost, so a cookie-only board is one no test can drive. The header costs a
// CORS preflight, cached an hour by the boxes' `access-control-max-age`.
export async function fleetReconcilerStatusApi(params) {
  return await doAxiosGet({
    url: `${ATLAS_FLEET_ENDPOINT}/ops/fleet/reconcile`,
    ...params,
  });
}

// Start a reconciler lap on ONE box. Not retried on failure, by rax's default
// httpMethodsToRetry (POST is absent from it) and deliberately so: a lap is a
// long-running origin-read sweep, and a retry that raced a slow-but-successful
// first attempt would be answered with the handler's own 409 anyway.
// Keyed by box NAME (`atlas-prod-eu-1`), not region: region is unique only within a
// stage -- alpha and prod both have one called "eu" -- so a region-keyed POST could
// start a sweep on the wrong box.
export async function runReconcilerLapApi({ box, ...params }) {
  return await doAxiosPost({
    url: `${ATLAS_FLEET_ENDPOINT}/ops/fleet/reconcile`,
    queryParams: { box },
    ...params,
  });
}

// OAuth consent page (containers/OAuthConsent.js). The authorization server is the
// EarningsCall/api service; these are the two calls its consent page contract defines
// ("The consent page contract (cca-app)" in that repo's documentation/mcp-server.md).
//
// What the user is being asked to approve: `{client_name, client_id, redirect_host,
// scopes: [{scope, description}], expires_at}`. A 400 means the request token is invalid
// or has expired. Takes no credential, so no `context` is passed and no session header
// goes out: the token in the query string is the whole key.
//
// A GET, so retry-axios (attached above) retries a 5xx, a 429 or a dropped connection. A
// 400 is not retried, and resending it would get the same answer.
export async function getOAuthAuthorizationRequest({ request, signal }) {
  return await doAxiosGet({
    url: `${OAUTH_API_ENDPOINT}/oauth/authorize/request`,
    queryParams: { request },
    signal,
  });
}

// The user's Allow or Deny. Answers `{redirect_to}`, the URL that sends the browser back
// to the app that asked, carrying a one-time code (allow) or an error (deny).
//
// The session goes in the `x-auth-type: session` / `x-auth-token` header pair, and that
// is the contract rather than a convenience: the API accepts no cookie on this route,
// because a header a page has to set on purpose is what keeps another site from
// submitting a decision for a signed-in visitor. So the context handed to
// extractAuthHeaders holds the session id and nothing else. It can produce the session
// pair or no credential at all, never one of the other auth types a sessionContext may
// also carry (google, test, ...).
//
// Not retried on failure, by rax's default httpMethodsToRetry (POST is absent from it):
// an Allow that reached the server has already issued a code.
export async function submitOAuthDecision({ request, decision, sessionId }) {
  return await doAxiosPost({
    url: `${OAUTH_API_ENDPOINT}/oauth/authorize/decision`,
    data: { request, decision },
    context: { sessionContext: { session: { session_id: sessionId } } },
  });
}

// Save transcript edits API.
//
// Was left on /a2 when #562 moved the rest of the Earnings domain to /a3 -- the
// transcript reader above migrated, this writer did not. The /a3 clone and its route
// (POST /a3/transcript/edit) have existed in prod since the expand phase and had served
// zero requests in the 90 days to 2026-09-17, while /a2 ran a flat ~10-15 calls/day. That
// kept the /a2 Lambda alive and out of reach of the contract phase, which is what finally
// noticed it.
export async function editTranscriptApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/transcript/edit`, ...params });
}

// Internal accounting: the chart of accounts.
//
// One URL, two verbs -- GET lists, POST dispatches on an `action` field
// ("seed-defaults", plus create/update/deactivate the M0 UI does not call yet).
// The backend gates every mutation on the admin role and answers 403 otherwise,
// so a BILLING user can read the chart but not seed it.
export async function accountingAccountsGet(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/accounting/accounts`, ...params });
}

export async function accountingAccountsPost(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/accounting/accounts`, ...params });
}

// Internal accounting: the money sources (Stripe, Mercury, Plaid) and what they
// expose. POST-only, dispatching on `action`: "list" reads every connection AND
// the external accounts behind them in one response, "sync-now" runs a bounded
// backfill inline and answers with the sync job's counts. Reads allow admin or
// billing; every mutation is admin-only server side and answers 403 otherwise.
export async function accountingConnectionsPost(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/accounting/connections`, ...params });
}

// Internal accounting: connecting a bank through Plaid. Two calls with a human
// in the middle -- "create-link-token" mints the token Plaid Link opens with,
// then "exchange-public-token" turns what Link hands back into a connection.
// Both actions write, so both are admin-only. `environment` is required on both
// ("test" | "production") and deliberately has no default: the wrong value here
// would connect the real bank.
export async function accountingPlaidLinkPost(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/accounting/plaid/link`, ...params });
}

// Internal accounting: the normalized transaction feed. GET with filter query
// params (external_account_id, from, to, categorization_status, limit, cursor)
// answering { events: [...], next_cursor: null | string }. Cursor-paged rather
// than offset-paged because it reads a DynamoDB index, so there is no page
// number to jump to -- only "the next page after this one".
export async function accountingEventsGet(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/accounting/events`, ...params });
}
